import { action, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';
import { DataSource, InMemoryDataSource, TableState } from '@servicetitan/table';

import {
    ApiGroup,
    AppClientResponse,
    APP_API_TOKEN,
    IAppApi,
    SecretManagementOption,
} from '../../api/app.api';

export interface AppClientResponseModel extends AppClientResponse {
    resourceOwnerText: string;
    clientIdText: string;
}

const createDataSource = (
    clients: AppClientResponse[],
): DataSource<AppClientResponseModel, string> => {
    return new InMemoryDataSource(
        clients.map(client => ({
            ...client,
            resourceOwnerText: client.environmentName
                ? `${client.resourceOwner} (${client.environmentName})`
                : client.resourceOwner,
            clientIdText: client.clientId ?? 'Not connected yet',
        })),
        model => `${model.resourceOwnerTerm ?? ''}|${model.resourceOwner}|${model.clientId ?? ''}`,
    );
};

@injectable()
export class AppClientListPageStore {
    @observable id = 0;
    @observable publicId = '';
    @observable appName = '';
    @observable.ref apiGroup: ApiGroup | null = null;
    @observable secretManagementOption: SecretManagementOption =
        SecretManagementOption.ResourceOwner;
    @observable tableState = new TableState<AppClientResponseModel, string>({ pageSize: 100 });
    @observable deleted = false;
    @observable errorBannerVisible = false;
    @observable loading = true;
    @observable selectedClient: AppClientResponse | null = null;

    constructor(@inject(APP_API_TOKEN) private readonly appApi: IAppApi) {
        makeObservable(this);
    }

    @action
    async load(id: number): Promise<void> {
        this.id = id;
        this.publicId = '';
        this.appName = '';
        this.apiGroup = null;
        this.secretManagementOption = SecretManagementOption.ResourceOwner;
        this.deleted = false;
        this.errorBannerVisible = false;
        this.loading = true;
        this.selectedClient = null;
        try {
            const config = await this.appApi.getApiConfiguration();
            const model = await this.appApi.getApp(id, { includeClients: true });
            runInAction(() => {
                this.publicId = model.publicId;
                this.appName = model.appName;
                this.apiGroup = config.apiGroups.find(
                    apiGroup => apiGroup.name === model.apiGroupName,
                )!;
                this.secretManagementOption = model.deleted
                    ? SecretManagementOption.ResourceOwner
                    : model.secretManagementOption;
                this.tableState.setDataSource(createDataSource(model.clients));
                this.deleted = model.deleted;
                this.loading = false;
            });
        } catch {
            runInAction(() => {
                this.errorBannerVisible = true;
                this.loading = false;
            });
        }
    }

    async refreshClients(): Promise<void> {
        const model = await this.appApi.getApp(this.id, { includeClients: true });
        this.tableState.setDataSource(createDataSource(model.clients));
        const { selectedClient } = this;
        if (selectedClient) {
            runInAction(() => {
                this.selectedClient =
                    model.clients.find(client => client.clientId === selectedClient.clientId) ??
                    null;
            });
        }
    }

    @action
    selectClient(client: AppClientResponse | null): void {
        this.selectedClient = client;
    }

    @action
    async generateClientSecret(): Promise<string> {
        const secretValue = await this.appApi.generateClientSecretAsDeveloper(
            this.id,
            this.selectedClient!.environmentId!,
            this.selectedClient!.clientId!,
        );
        await this.refreshClients();
        return secretValue;
    }

    @action
    async deactivateClientSecret(secretId: string): Promise<void> {
        await this.appApi.deactivateClientSecretAsDeveloper(
            this.id,
            this.selectedClient!.environmentId!,
            this.selectedClient!.clientId!,
            secretId,
        );
        await this.refreshClients();
    }
}
